import React, { useState, useEffect } from 'react';
import { FaCheck, FaExclamationTriangle, FaSpinner, FaEnvelope } from 'react-icons/fa';
import { unsubscribeFromBlog } from '../api/client';
import SEO from './SEO';
import { SEO_BASE_URL } from '../config';

const BlogUnsubscribe = ({ token, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid unsubscribe link');
      setLoading(false);
      return;
    }

    const processUnsubscribe = async () => {
      try {
        const result = await unsubscribeFromBlog(token);
        setSuccess(true);
        setEmail(result.email || '');
      } catch (err) {
        const message = err.response?.data?.detail || 'Failed to unsubscribe. The link may be invalid or expired.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    processUnsubscribe();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-primary-600 mx-auto mb-4" />
          <p className="text-neutral-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <SEO
        title="Unsubscribe from Blog Updates"
        description="Unsubscribe from ItinerarySeek blog newsletter"
        canonical={`${SEO_BASE_URL}/unsubscribe`}
      />

      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {success ? (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheck className="text-green-600 text-3xl" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-800 mb-3">
              You've Been Unsubscribed
            </h1>
            <p className="text-neutral-600 mb-6">
              {email && <><strong>{email}</strong> has been removed from our blog mailing list. </>}
              You will no longer receive email notifications about new articles.
            </p>
            <p className="text-sm text-neutral-500 mb-6">
              Changed your mind? You can always resubscribe by visiting any of our blog articles.
            </p>
            <button
              onClick={onBack}
              className="inline-block px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              Return to ItinerarySeek
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaExclamationTriangle className="text-red-600 text-3xl" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-800 mb-3">
              Unsubscribe Failed
            </h1>
            <p className="text-neutral-600 mb-6">
              {error}
            </p>
            <p className="text-sm text-neutral-500 mb-6">
              If you continue to receive unwanted emails, please contact us at{' '}
              <a href="mailto:info@itineraryseek.com" className="text-primary-600 hover:underline">
                info@itineraryseek.com
              </a>
            </p>
            <button
              onClick={onBack}
              className="inline-block px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              Return to ItinerarySeek
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BlogUnsubscribe;
